import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDateIT(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return "";
  }
}

function FreshnessBadge({ live, createdAt }) {
  if (live) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium"
        title="Questo report mostra l'ultima versione del piano di sviluppo. Approvazioni e modifiche sono visibili automaticamente."
        data-testid="shared-badge-live"
      >
        <span className="relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
        </span>
        Aggiornato in tempo reale
      </div>
    );
  }
  return (
    <div className="flex flex-col items-end gap-0.5">
      <div
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#0D1F3C]/5 border border-[#0D1F3C]/15 text-[#0D1F3C]/70 text-xs font-medium"
        title="Il dipendente originale non è più disponibile. Stai vedendo una versione storica del report."
        data-testid="shared-badge-archived"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 8v13H3V8M1 3h22v5H1zM10 12h4" />
        </svg>
        Versione archiviata
      </div>
      {createdAt && (
        <span className="text-[10px] text-[#0D1F3C]/50">Snapshot del {formatDateIT(createdAt)}</span>
      )}
    </div>
  );
}

export default function SharedReportPage() {
  const { shareId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/shared-report/${shareId}`)
      .then(r => setReport(r.data))
      .catch(() => setError("Report non trovato"))
      .finally(() => setLoading(false));
  }, [shareId]);

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]"><div className="animate-spin w-8 h-8 border-2 border-[#1B4FD8] border-t-transparent rounded-full" /></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen bg-[#F4F7FB]"><p className="text-[#0D1F3C]/50">{error}</p></div>;

  return (
    <div className="min-h-screen bg-[#F4F7FB] p-8" data-testid="shared-report-page">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
          <h1 className="text-xl font-bold text-[#0D1F3C]">SELLY<span className="text-[#56A3F5]">®</span> <span className="text-base font-normal text-[#0D1F3C]/50 ml-2">Report — {report?.name}</span></h1>
          <div className="flex items-center gap-3">
            <FreshnessBadge live={report?.live === true} createdAt={report?.created_at} />
            <button onClick={() => window.print()} className="px-4 py-2 bg-[#1B4FD8] text-white text-sm rounded-lg hover:bg-[#0D1F3C] transition-all" data-testid="shared-print-btn">Stampa / PDF</button>
          </div>
        </div>
        <Card className="border-[#0D1F3C]/8">
          <CardContent className="p-8">
            {report?.html && <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: report.html }} />}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
